import { isNull, location, product, sql, stock_picking, warehouse } from "@stockpilot/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)               │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for the 4 tables with trash in this repo (verified
 * reading `packages/powersync/src/schema.ts`, not assumed):
 *   product, warehouse, location, stock_picking.
 * Every live read of these tables composes these predicates (the builders in
 * `inventory/queries.ts` and any other live view import them from here). The
 * ONLY reads that see deleted rows are the trash
 * (`inventory/queries.ts#getTrashed*Query`, whitelisted as "trash") and any
 * harmless id-scoped lookup or activity feed name resolution
 * (whitelisted as "keyed"/"trash" in `guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant already caused real bugs in this
 * repo (see guard-manifest.ts): the `checkUnsyncedActor` counter counted
 * products in trash, the ⌘K search showed them in results, and
 * `getReceiptsQuery` (receipts for landed costs) did not exclude archived
 * operations.
 *
 * ─── Cohort hierarchy (Stockpilot inventory) ──────────────────────────────
 * Verified against the LOGICAL FKs in `packages/powersync/src/schema.ts` (there
 * are no physical FKs in Postgres: they are modeled logically, same as in
 * comandero/meal-planner):
 *   - product          → ROOT. `category_id`→product_category and `uom_id`→uom are
 *                        NOT soft-delete, so there is no parent cohort.
 *   - warehouse        → ROOT (only `organization_id`).
 *   - location         → child of warehouse (`warehouse_id`). Also has
 *                        `parent_id` (internal hierarchy, self-referential),
 *                        but see the PowerSync gotcha below: it is NOT
 *                        implemented as a self-join cohort.
 *   - stock_picking    → child of warehouse (`warehouse_id`) and location, twice
 *                        (`src_location_id` origin, `dst_location_id`
 *                        destination) — both optional depending on the operation type
 *                        (a receipt has no origin, e.g.), so the
 *                        NOT EXISTS evaluates to true when the FK is NULL.
 *
 * NOTE (single-level cohort, pilot/comandero/meal-planner pattern): each
 * cohort checks the DIRECT PARENT.
 *
 * DISCOVERED GOTCHA (PowerSync, not a theoretical limit — it blew up in
 * runtime): a self-join cohort was attempted for `location.parent_id` via
 * `alias(location, "location_parent")` (drizzle). It compiled and the SQL was
 * correct for SQLite, but PowerSync's `resolveTables` (the mechanism that
 * decides which tables to observe to invalidate reactive queries) scans the SQL
 * looking for table names and does NOT understand the alias — it treats it as a
 * literal table and explodes at runtime: `Error: no such table: location_parent`
 * (observed in the browser against the real local DB, not in the `bun test`
 * tests with `drizzle-orm/sqlite-proxy`, which only compile to SQL and never execute).
 * This completely broke `getLocationsQuery` (no location appeared).
 * CONCLUSION: in repos on PowerSync, self-join cohorts with aliases are not
 * viable — it was removed. The `location.parent_id` cohort remains as a
 * known limit (unimplemented): a sub-location of a location in trash is
 * not hidden through that path (only through its own `deleted_at` or through the
 * `warehouse_id` cohort). In practice the catalog location hierarchy is
 * flat (1-2 levels) and the edge case has not been observed.
 */

/**
 * Generic cohort predicate: the child row belongs to a parent that is NOT in
 * the trash. With parent soft-delete the children remain intact (to restore
 * the entire cohort) but disappear from live views. If `childFk` is NULL
 * (optional FK, e.g. `src_location_id` of a receipt), the NOT EXISTS resolves
 * to true — does not hide the row.
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Roots (only their own deleted_at) ──────────────────────────────────────

/** LIVE products (no cohort: category_id/uom_id are not soft-delete). */
export function liveProductFilters() {
  return [isNull(product.deleted_at)];
}

/** LIVE warehouses (root of the location hierarchy). */
export function liveWarehouseFilters() {
  return [isNull(warehouse.deleted_at)];
}

// ─── Children with cohort (parent with trash not deleted) ───────────────────

/**
 * LIVE locations: their own `deleted_at` + their warehouse is not in trash.
 * (Does NOT include `parent_id` cohort — see PowerSync gotcha in module
 * comment: an aliased self-join breaks `resolveTables` at runtime.)
 */
export function liveLocationFilters() {
  return [isNull(location.deleted_at), parentNotTrashed(warehouse, location.warehouse_id)];
}

/**
 * LIVE operations (pickings): their own `deleted_at` + their warehouse and their
 * origin/destination locations (when they exist) are not in trash.
 */
export function liveStockPickingFilters() {
  return [
    isNull(stock_picking.deleted_at),
    parentNotTrashed(warehouse, stock_picking.warehouse_id),
    parentNotTrashed(location, stock_picking.src_location_id),
    parentNotTrashed(location, stock_picking.dst_location_id),
  ];
}
